import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FileX, Loader2 } from 'lucide-react';
import { ViewMode, Meeting } from './types';
import type { AudioImportFile, CalendarEventDTO } from './global';
import { Sidebar } from './components/Sidebar';
import { TopBar } from './components/TopBar';
import { GateFrame } from './components/GateFrame';
import { HomeView } from './components/HomeView';
import { CraftNoteView } from './components/CraftNoteView';
import { DailyDigestView } from './components/DailyDigestView';
import { AIChatView } from './components/AIChatView';
import { useChatHistory } from './features/chat/useChatHistory';
import { PeopleView } from './components/PeopleView';
import { PersonProfileView } from './components/PersonProfileView';
import { CompaniesView } from './components/CompaniesView';
import { CompanyProfileView } from './components/CompanyProfileView';
import { IntegrationsView } from './components/IntegrationsView';
import { TrashView } from './components/TrashView';
import { Omnibar } from './components/Omnibar';
import { Toast, type ToastMessage } from './components/Toast';
import { SignInView } from './features/auth/SignInView';
import { SettingsView } from './features/settings/SettingsView';
import { RecordingView } from './features/recording/RecordingView';
import { RecordingTopBar } from './features/recording/RecordingTopBar';
import { useRecordingSession, type RecordPrefill } from './features/recording/useRecordingSession';
import { uploadPhaseLabel, useAudioUpload } from './features/upload/useAudioUpload';
import { useAutoStop } from './features/recording/useAutoStop';
import { AutoStopBanner } from './features/recording/AutoStopBanner';
import { useAuth } from './features/auth/useAuth';
import { useOnboarding } from './features/onboarding/useOnboarding';
import { OnboardingView } from './features/onboarding/OnboardingView';
import { UpdateRequiredView } from './features/updates/UpdateRequiredView';
import { useMeetings } from './features/meetings/useMeetings';
import { useSummaryEmail } from './features/email/useSummaryEmail';
import { draftToEmailPayload, meetingToEmailPayload } from './features/email/summaryEmail';
import { SendBotDialog } from './features/bot/SendBotDialog';
import { ShareMeetingDialog } from './features/meetings/ShareMeetingDialog';
import { FolderView } from './features/folders/FolderView';
import { useFolders } from './features/folders/useFolders';
import { SettingsPage, type SettingsSectionId } from './features/preferences/SettingsPage';
import { MIN_SUMMARY_SECONDS } from './features/recording/summaryRules';
import { useLanguage } from './lib/i18n';
import { useTheme } from './lib/theme';
import { usePreferences } from './features/preferences/usePreferences';
import { playRecordingCue } from './features/recording/recordingSounds';
import { RecordSourceDialog } from './features/recording/RecordSourceDialog';
import { canCaptureTabAudio, IS_WEB } from './lib/platform';
import { parsePath, useWebLocation } from './lib/webLocation';
import { useIsNarrow } from './lib/useIsNarrow';
import { MobileTabBar, type MobileTab } from './features/mobile/MobileTabBar';
import { MobileHomeView } from './features/mobile/MobileHomeView';
import { MobileCalendarView } from './features/mobile/MobileCalendarView';
import { MobileMoreView } from './features/mobile/MobileMoreView';
import { MobileRecordSheet } from './features/mobile/MobileRecordSheet';
import { MobileRecordingControls, MobileRecordingStrip, MobileRecordingTopBar } from './features/mobile/MobileRecordingBars';
import { MobileBackBar } from './features/mobile/MobileUI';
import { isTourPending, markTourDone, markTourPending, ProductTour, type TourStep } from './features/onboarding/ProductTour';

export default function App() {
  // Dil değişince bütün ağaç yeniden çizilsin diye App dili okuyor.
  const { t, locale } = useLanguage();
  const auth = useAuth();
  const isSignedIn = auth.status === 'signed_in';
  const onboarding = useOnboarding(isSignedIn ? auth.user?.id ?? null : null);

  const [currentView, setCurrentView] = useState<ViewMode>('home');
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  /** Kişi/şirket düzenlendiğinde listeler tazelensin. */
  const [directoryBump, setDirectoryBump] = useState(0);

  // UI states
  /**
   * Telefon düzeni (mobil uygulama ve web'de dar ekran): kenar çubuğu ve üst
   * çubuk yerine alttaki sekmeler, her ekranda büyük başlık, pencereler alttan.
   */
  const isMobile = useIsNarrow();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  /** Telefon: ortadaki kayıt düğmesinin alt sayfası. */
  const [isRecordSheetOpen, setIsRecordSheetOpen] = useState(false);
  /** Telefon: toplantıdan, kayıttan ya da ayarlardan "geri" ile dönülecek ekran. */
  const [returnView, setReturnView] = useState<ViewMode>('home');
  // Tema artık ayarlarda: sağlayıcı hem seçimi hem işletim sistemi temasını biliyor.
  const { isDarkMode, setTheme } = useTheme();
  const toggleTheme = useCallback(() => void setTheme(isDarkMode ? 'light' : 'dark'), [isDarkMode, setTheme]);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [isBotDialogOpen, setIsBotDialogOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  /** Web: kayıt kaynağı penceresi açıkken bekleyen ön doldurma (bkz. requestRecording). */
  const [sourceChoice, setSourceChoice] = useState<{ prefill?: RecordPrefill } | null>(null);
  /** Ayarlar belirli bir bölümle açılırsa (ör. sitenin plan düğmesinden Abonelik). */
  const [settingsEntry, setSettingsEntry] = useState<{ section: SettingsSectionId } | null>(null);
  /** Ctrl+Alt+Shift+N ile not alanına odaklanma isteği — her basışta artıyor. */
  const [notesFocusSignal, setNotesFocusSignal] = useState(0);

  // shadcn tarzı bileşenlerin renk token'ları temayı buradan okuyor (index.css).
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = isDarkMode ? 'dark' : 'light';
  }, [isDarkMode]);

  const showToast = useCallback((next: Omit<ToastMessage, 'id'>) => {
    setToast({ ...next, id: Date.now() });
  }, []);

  const notify = useCallback(
    (kind: ToastMessage['kind'], text: string) => showToast({ kind, text }),
    [showToast]
  );

  const meetingsStore = useMeetings(isSignedIn);
  const foldersStore = useFolders(isSignedIn);
  const { meetings } = meetingsStore;
  const { prefs } = usePreferences();
  const behavior = prefs.behavior;

  /**
   * Kayıt oturumu App seviyesinde yaşıyor: kayıt artık bir modal değil bir
   * sayfa, yani kullanıcı kayıt sürerken Home'a geçebilmeli ve mikrofon
   * kapanmamalı. Kanca sayfanın içinde olsaydı sayfadan çıkmak kaydı keserdi.
   */
  const recording = useRecordingSession();
  const audioUpload = useAudioUpload();

  // Yeni bir toplantı kaydedildiğinde ya da kişi/şirket düzenlendiğinde
  // Kişiler/Şirketler ekranları tazelensin.
  const directoryRevision = meetings.length * 1000 + directoryBump;
  const bumpDirectory = useCallback(() => setDirectoryBump((v) => v + 1), []);
  const summaryEmail = useSummaryEmail(isSignedIn);

  /**
   * Seçili toplantı kimlikten türetiliyor; nesne kopyası tutulmuyor.
   * Böylece depo güncellendiğinde (örneğin sunucu gerçek kimliği döndürdüğünde)
   * açık olan not de kendiliğinden tazeleniyor — eski kod iki ayrı kopyayı
   * elle senkron tutmak zorundaydı.
   */
  const selectedMeeting = useMemo(
    () => meetings.find((m) => m.id === selectedMeetingId) ?? null,
    [meetings, selectedMeetingId]
  );
  // Henüz kaydedilmemiş (geçici kimlikli) toplantı paylaşılamaz; masaüstü köprüsünde paylaşım yok.
  const shareMeeting =
    window.zabit.meetingShare && selectedMeeting && /^[0-9a-f-]{36}$/i.test(selectedMeeting.id) ? () => setIsShareOpen(true) : undefined;
  const selectedFolder = foldersStore.folders.find((folder) => folder.id === selectedFolderId) ?? null;

  const handleSelectFolder = (folderId: string) => {
    setSelectedMeetingId(null);
    setSelectedFolderId(folderId);
    setCurrentView('folder');
  };

  const handleMoveMeeting = (meetingId: string, folderId: string | null) => {
    void meetingsStore.moveToFolder(meetingId, folderId).catch((err: Error) => showToast({ kind: 'error', text: err.message }));
  };

  const handleSelectMeeting = (meeting: Meeting) => {
    if (currentView !== 'meeting') setReturnView(currentView);
    setSelectedMeetingId(meeting.id);
    setCurrentView('meeting');
  };

  /** Profil sayfalarından (kişi/şirket) bir toplantıya geçiş. */
  const handleOpenMeetingById = (meetingId: string) => {
    if (currentView !== 'meeting') setReturnView(currentView);
    setSelectedMeetingId(meetingId);
    setCurrentView('meeting');
  };

  const handleOpenPerson = (personId: string) => {
    setSelectedPersonId(personId);
    setSelectedMeetingId(null);
    setCurrentView('person');
  };

  const handleOpenCompany = (companyId: string) => {
    setSelectedCompanyId(companyId);
    setSelectedMeetingId(null);
    setCurrentView('company');
  };

  const handleBackToHome = () => {
    setSelectedMeetingId(null);
    // Telefonda geri, toplantının açıldığı sekmeye (Takvim, Sohbet…) döner; klasörden açıldıysa klasöre.
    setCurrentView(isMobile || returnView === 'folder' ? returnView : 'home');
  };

  /** Telefon: ayarları aç — profil fotoğrafından doğrudan Genel, "Daha fazla"dan liste. */
  const openSettings = (section?: SettingsSectionId) => {
    setReturnView(currentView);
    setSettingsEntry(section ? { section } : null);
    setCurrentView('preferences');
  };

  const handleUpdateMeeting = (updated: Meeting) => {
    void meetingsStore.updateMeeting(updated);
  };

  /**
   * "Sil" toplantıyı çöp kutusuna taşır (30 gün geri alınabilir). Bildirimdeki
   * "Geri al" hemen geri getirir; sonra da Çöp kutusu ekranından alınabilir.
   */
  const handleDeleteMeetings = (ids: string[]) => {
    void meetingsStore.deleteMeetings(ids).then((ok) => {
      if (!ok) return;
      const persisted = ids.filter((id) => !id.startsWith('temp-'));
      showToast({
        kind: 'info',
        text:
          ids.length > 1
            ? t(`${ids.length} toplantı çöp kutusuna taşındı.`, `${ids.length} meetings moved to the trash.`)
            : t('Toplantı çöp kutusuna taşındı.', 'Meeting moved to the trash.'),
        ...(persisted.length > 0
          ? {
              action: {
                label: t('Geri al', 'Undo'),
                onClick: () => {
                  // Ana süreç geri alınca meetingsChanged yayınlıyor; liste sessizce tazelenir.
                  window.zabit
                    .restoreMeetings(persisted)
                    .catch((err: Error) => showToast({ kind: 'error', text: err.message }));
                },
              },
            }
          : {}),
      });
    });
    if (selectedMeetingId && ids.includes(selectedMeetingId)) {
      setSelectedMeetingId(null);
      setCurrentView('home');
    }
  };

  /** Boş bir Craft notu — kayıt olmadan, doğrudan yazmak için. */
  const handleCreateNewNote = async () => {
    const now = new Date();
    const clock = now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    const untitled = t('İsimsiz Not', 'Untitled note');
    const draft: Omit<Meeting, 'id'> = {
      title: untitled,
      recordedAtISO: now.toISOString(),
      date: t('Bugün', 'Today'),
      time: clock,
      timeRange: clock,
      duration: t('0 dk', '0 min'),
      status: 'in-progress',
      space: 'private',
      participants: [],
      tags: [t('Not', 'Note')],
      summary: {
        tldr: t(
          'Bu not kişisel düşünceler ve serbest bloklar için oluşturuldu.',
          'This note is for personal thoughts and free-form blocks.'
        ),
        keyPoints: [],
        decisions: []
      },
      actionItems: [],
      craftBlocks: [
        { id: `cb-${Date.now()}-1`, type: 'heading', content: untitled },
        {
          id: `cb-${Date.now()}-2`,
          type: 'callout',
          content: t(
            '💡 "Blok Ekle" ile başlık, madde, yapılacak veya alıntı ekleyebilirsiniz.',
            '💡 Use "Add block" to insert headings, bullets, to-dos or quotes.'
          )
        }
      ],
      transcript: [],
      audioDurationSeconds: 0,
      storage: 'local'
    };

    const saved = await meetingsStore.addMeeting(draft);
    if (saved) {
      setSelectedMeetingId(saved.id);
      setCurrentView('meeting');
    }
  };

  // — Kayıt akışı —

  /**
   * Kayıt zaten sürüyorsa yeni kayıt başlatmak yerine sayfasına dönülür;
   * aynı anda iki kayıt olamaz (`begin` de bunu ayrıca koruyor).
   */
  const openRecording = useCallback(
    (prefill?: RecordPrefill) => {
      // Telefonda kayıt küçültülünce ve not açıldıktan sonra geri, başlatılan ekrana döner.
      if (currentView !== 'recording' && currentView !== 'meeting' && currentView !== 'preferences') setReturnView(currentView);
      setSelectedMeetingId(null);
      setCurrentView('recording');
      if (!recording.isActive) void recording.begin(prefill);
    },
    [recording, currentView]
  );

  /**
   * "Kayıt al" düğmelerinin girişi. Web'de (Chrome/Edge) önce kaynak sorulur:
   * yalnızca mikrofon mu, toplantı sekmesinin sesi de mi (RecordSourceDialog).
   * Kayıt zaten sürüyorsa doğrudan sayfasına gidilir.
   */
  const requestRecording = useCallback(
    (prefill?: RecordPrefill) => {
      if (IS_WEB && !recording.isActive && canCaptureTabAudio()) {
        setSourceChoice({ prefill });
        return;
      }
      openRecording(prefill);
    },
    [openRecording, recording.isActive]
  );

  const eventPrefill = (event: CalendarEventDTO): RecordPrefill => ({
    title: event.title,
    // Bitiş saati otomatik durdurmaya gidiyor: saat geçip ses kesilince
    // kayıt daha kısa bir sessizlikten sonra bitirilir. Tüm gün etkinliklerinin
    // bitişi anlamlı bir toplantı sonu değil.
    endsAt: event.isAllDay ? undefined : event.endISO,
    // Takvim katılımcıları kayda aktarılır — Kişiler/Şirketler ekranları bunlardan beslenir.
    participants: (event.attendees ?? []).map((a) => ({
      name: a.name || a.email.split('@')[0],
      email: a.email,
    })),
  });

  const handleStartRecordingForEvent = (event: CalendarEventDTO) => requestRecording(eventPrefill(event));

  /**
   * Takvim kartındaki mikrofon: bot göndermeden yalnızca sistem sesi (karşı
   * taraf) kaydedilir. Web'de bu, paylaşılan toplantı sekmesinin sesi —
   * tarayıcı sekme sesi veremiyorsa kayıt başlamaz.
   */
  const handleRecordSystemAudioForEvent = (event: CalendarEventDTO) => {
    if (!recording.isActive && IS_WEB && !canCaptureTabAudio()) {
      showToast({
        kind: 'error',
        text: t(
          'Bu tarayıcı toplantının sesini alamıyor. Chrome ya da Edge kullanabilir ya da toplantıya bot gönderebilirsin.',
          'This browser can’t capture the meeting’s audio. Use Chrome or Edge, or send a bot to the meeting.'
        ),
      });
      return;
    }
    openRecording({ ...eventPrefill(event), captureSystemAudio: true, systemOnly: true });
  };

  /**
   * Ana ekrandaki "Ses dosyası yükle": dosya yazıya dökülüp özetlenir ve
   * kayıt gibi kaydedilir. Sürerken üst çubuktaki düğme aşamayı gösteriyor.
   */
  const handleUploadAudio = async (preset?: AudioImportFile, presetKind: 'recovered' | 'shared' = 'recovered') => {
    const outcome = await audioUpload.run(preset);
    if (outcome.kind === 'cancelled') return;
    if (outcome.kind === 'error') {
      showToast({ kind: 'error', text: outcome.message });
      return;
    }
    const saved = await meetingsStore.addMeeting(outcome.draft);
    if (!saved) {
      // Kaydedilemedi (depo hatası zaten bildirildi) — dönüştürülen dosya diskte kalmasın.
      void window.zabit.discardAudioFile(outcome.filePath).catch(() => {});
      return;
    }
    showToast({
      kind: 'success',
      text: preset && presetKind === 'recovered'
        ? t('Yarıda kalan kayıt kurtarıldı, yazıya döküldü ve özetlendi.', 'The interrupted recording was recovered, transcribed and summarized.')
        : t('Ses dosyası yazıya döküldü ve özetlendi.', 'The audio file was transcribed and summarized.'),
    });
    setSelectedMeetingId(saved.id);
    setCurrentView('meeting');
  };

  // — Toplantı botu (Skribby) —

  // Web: tanıtım sitesinin plan düğmeleri `?abonelik` ile gelir (giriş bu adresi korur) —
  // girişten sonra Ayarlar > Abonelik açılır.
  useEffect(() => {
    if (!IS_WEB || !isSignedIn) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has('abonelik')) return;
    params.delete('abonelik');
    const query = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
    setSettingsEntry({ section: 'subscription' });
    setCurrentView('preferences');
  }, [isSignedIn]);

  const handleSendBotFromDialog = async (meetingUrl: string, title: string) => {
    await window.zabit.sendBot({ meetingUrl, title: title || undefined });
    showToast({
      kind: 'success',
      text: t(
        'Bot toplantıya katılıyor. Toplantı bitince özet hazırlanacak.',
        'The bot is joining the meeting. The summary will be ready when it ends.'
      ),
    });
  };

  const handleStopRecording = async (isAutomatic = false) => {
    // Otomatik durdurma ile elle durdurma aynı anda tetiklenirse ikinci
    // çağrı zaten durmakta olan kaydı yeniden durdurmaya çalışmasın.
    if (recording.phase !== 'live') return;
    const draft = await recording.stopAndSave();

    if (isAutomatic) {
      showToast({
        kind: 'info',
        text: t(
          'Toplantı bittiği için kayıt otomatik olarak bitirildi.',
          'The meeting ended, so the recording was stopped automatically.'
        ),
      });
    }

    // Taslak üretilemediyse kayıt sayfasında kal: hata mesajı orada
    // gösteriliyor ve "Tekrar dene" düğmesi orada. Eskiden burada koşulsuz
    // Home'a gidiliyordu, yani kullanıcı neyin ters gittiğini hiç görmüyordu.
    if (!draft) {
      if (!recording.error) setCurrentView('home');
      return;
    }

    const saved = await meetingsStore.addMeeting(draft);
    if (saved) {
      if (behavior.recordingSounds) playRecordingCue('saved');
      setSelectedMeetingId(saved.id);
      setCurrentView('meeting');
    } else {
      if (behavior.recordingSounds) playRecordingCue('error');
      setCurrentView('home');
    }

    // Özet e-postası. Veritabanına yazmadan BAĞIMSIZ: kayıt Supabase'e
    // yazılamasa bile kullanıcı özetini gelen kutusunda bulsun. Gönderim
    // kullanıcıyı bekletmiyor, sonucu bildirimle söyleniyor.
    // Kısa kayıtlar özetlenmediği için e-postada anlatılacak bir şey yok.
    // Kayıt yazılabildiyse kimliği gider: "Herkese" açıksa katılımcı adresleri
    // ana süreçte veritabanından okunur.
    if (draft.audioDurationSeconds >= MIN_SUMMARY_SECONDS) void deliverSummaryEmail(draft, saved?.id);
  };

  /**
   * Kayıt bitince özeti Ayarlar > Takvim'deki seçime göre yollar: kendine
   * ve/veya toplantıdaki herkese. Ayar kapalıysa ana süreç `disabled`
   * döndürüyor ve sessizce geçiliyor — kapalı bir özellik için bildirim
   * göstermek gürültü olur.
   */
  const deliverSummaryEmail = async (draft: Omit<Meeting, 'id'>, meetingId?: string) => {
    const result = await summaryEmail.send(draftToEmailPayload(draft), false, meetingId);

    if (result.ok) {
      showToast({
        kind: 'success',
        text:
          result.recipientCount > 1
            ? t(`Toplantı özeti ${result.recipientCount} kişiye gönderildi.`, `Meeting summary sent to ${result.recipientCount} people.`)
            : t(`Toplantı özeti ${result.recipient} adresine gönderildi.`, `Meeting summary sent to ${result.recipient}.`),
      });
      return;
    }

    if (result.reason === 'disabled') return;

    showToast({ kind: 'error', text: result.message });
  };

  /** Not sayfasındaki "E-posta Gönder" — otomatik gönderim kapalıyken de yollar. */
  const handleSendSummaryEmail = (meeting: Meeting) =>
    summaryEmail.send(meetingToEmailPayload(meeting), true);

  const handleDiscardRecording = async () => {
    const confirmed = window.confirm(
      IS_WEB
        ? t('Kayıt silinecek ve saklanmayacak. Emin misiniz?', 'The recording will be discarded and won’t be saved. Are you sure?')
        : t('Kayıt silinecek ve diske yazılmayacak. Emin misiniz?', 'The recording will be discarded and won’t be saved. Are you sure?')
    );
    if (!confirmed) return;
    await recording.cancel();
    setCurrentView('home');
  };

  // Toplantı bitince kayıt sonsuza kadar sürmesin — bkz. useAutoStop.
  const autoStop = useAutoStop(recording, () => void handleStopRecording(true), {
    callEndRules: prefs.meetings.autoStopOnCallEnd,
  });

  /**
   * Genel kısayollar ana süreçten geliyor (bkz. services/behavior.ts). Abonelik
   * bir kez kuruluyor; güncel işleyicilere ref üzerinden ulaşılıyor, yoksa
   * kapanış eski kaydı görürdü.
   */
  const shortcutRef = useRef({ recording, openRecording, handleStopRecording });
  shortcutRef.current = { recording, openRecording, handleStopRecording };

  useEffect(() => {
    return window.zabit.onShortcutAction((action) => {
      const current = shortcutRef.current;
      if (action === 'toggle-recording') {
        if (current.recording.phase === 'live') void current.handleStopRecording();
        else current.openRecording();
        return;
      }
      if (action === 'recording-notes') {
        // Kayıt yoksa not alacak bir şey de yok.
        if (!current.recording.isActive) return;
        setCurrentView('recording');
        setNotesFocusSignal((value) => value + 1);
        return;
      }
      // quick-find: liste ekranına dön ve arama çubuğuna odaklan.
      setSelectedMeetingId(null);
      setCurrentView('home');
      window.setTimeout(() => document.getElementById('omnibar-input')?.focus(), 0);
    });
  }, []);

  // "Daha fazla" yalnızca telefonun sekmesi; geniş ekranda karşılığı kenar çubuğu.
  useEffect(() => {
    if (!isMobile && currentView === 'more') setCurrentView('home');
  }, [isMobile, currentView]);

  // Sunucu bu sürümü artık kabul etmiyor (426): güncelleme ekranı açılır.
  // undefined = gerek yok; null = gerekli ama en düşük sürüm bilinmiyor.
  const [updateRequired, setUpdateRequired] = useState<string | null | undefined>(undefined);
  useEffect(() => window.zabit.onUpdateRequired((minVersion) => setUpdateRequired(minVersion)), []);

  // Güncelleme arka planda indiğinde bir kez haber ver; kurulum kullanıcının
  // düğmesiyle (ya da uygulamadan çıkarken) olur, kayıt yarıda kesilmez.
  const announcedUpdateRef = useRef<string | null>(null);
  useEffect(() => {
    return window.zabit.onUpdateStatus((status) => {
      if (status.state !== 'ready' || !status.version || announcedUpdateRef.current === status.version) return;
      announcedUpdateRef.current = status.version;
      showToast({
        kind: 'info',
        text: t(
          `TalkNote ${status.version} hazır. Yeniden başlatınca kurulur.`,
          `TalkNote ${status.version} is ready. It installs when you restart.`
        ),
        action: {
          label: t('Yeniden başlat', 'Restart'),
          onClick: () => {
            window.zabit.installUpdate().catch((err: Error) => showToast({ kind: 'error', text: err.message }));
          },
        },
      });
    });
  }, [showToast, t]);

  // Kayıt durumu ana sürece bildiriliyor (hatırlatıcı ve görüşme izleyici için).
  useEffect(() => {
    window.zabit.setRecordingWindowMode(recording.isActive);
  }, [recording.isActive]);

  // Web: kayıt sürerken sekme kapatılır ya da yenilenirse tarayıcı sorsun.
  // (Kapatılsa bile ses parçaları tarayıcıda; bir sonraki açılışta kurtarılır.)
  useEffect(() => {
    if (!IS_WEB || !recording.isActive) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [recording.isActive]);

  // Web: oturum açılıp kurulum bitince bir kez — takvim bağlamadan dönüşün
  // sonucu ve önceki oturumda yarıda kalmış kayıtlar (yazıya dökülüp not olur).
  const isReady = isSignedIn && onboarding.status === 'done';
  const uploadRef = useRef(handleUploadAudio);
  uploadRef.current = handleUploadAudio;
  useEffect(() => {
    if (!IS_WEB || !isReady) return;
    void (async () => {
      const connected = (await window.zabit.takeCalendarConnectReturn?.()) ?? null;
      if (connected) {
        const name = connected.provider === 'google' ? 'Google Takvim' : 'Outlook';
        showToast(
          connected.ok
            ? { kind: 'success', text: t(`${name} bağlandı.`, `${name} connected.`) }
            : {
                kind: 'error',
                text:
                  connected.error === 'izin'
                    ? t(`${name} için takvim izni verilmedi.`, `Calendar access for ${name} was not granted.`)
                    : t(`${name} bağlanamadı, tekrar dene.`, `Could not connect ${name}. Please try again.`),
              }
        );
      }
      const recovered = (await window.zabit.takeRecoveredRecordings?.()) ?? [];
      for (const file of recovered) await uploadRef.current(file);
    })();
  }, [isReady, showToast, t]);

  // Mobil: paylaş menüsünden gelen ses dosyası (açılışta ya da uygulama açıkken)
  // yazıya dökülüp not olur.
  useEffect(() => {
    const shared = window.zabit.sharedAudio;
    if (!shared || !isReady) return;
    const receive = async () => {
      try {
        const file = await shared.take();
        if (file) await uploadRef.current(file, 'shared');
      } catch (err) {
        showToast({ kind: 'error', text: (err as Error).message });
      }
    };
    void receive();
    return shared.onShared(() => void receive());
  }, [isReady, showToast]);

  // Web: ekran ↔ adres (geri tuşu, yenileme, paylaşılan toplantı bağlantısı).
  useWebLocation(
    { view: currentView, meetingId: selectedMeetingId, personId: selectedPersonId, companyId: selectedCompanyId, folderId: selectedFolderId },
    (next) => {
      setCurrentView(next.view);
      setSelectedMeetingId(next.meetingId);
      if (next.folderId) setSelectedFolderId(next.folderId);
      if (next.personId) setSelectedPersonId(next.personId);
      if (next.companyId) setSelectedCompanyId(next.companyId);
    },
    isReady
  );

  /** Çıkışta adres ve ekran sıfırlansın: sonraki giriş Ayarlar'da değil Toplantılar'da açılsın. */
  const signOut = useCallback(() => {
    if (IS_WEB) window.history.replaceState(null, '', '/');
    setCurrentView('home');
    setSelectedMeetingId(null);
    void auth.signOut();
  }, [auth]);

  /**
   * İlk kurulumun sonu ("Kullanmaya başla"): Toplantılar ekranı açılır — adreste kalmış
   * eski bir ekran (ör. çıkış yapılan /ayarlar) değil; paylaşılan toplantı bağlantısı
   * korunur. Ürün turu bu kullanıcı için bu cihazda bir kez gösterilecek diye işaretlenir.
   */
  const completeOnboarding = useCallback(async () => {
    if (IS_WEB && parsePath(window.location.pathname).view !== 'meeting') window.history.replaceState(null, '', '/');
    setCurrentView('home');
    if (auth.user?.id) markTourPending(auth.user.id);
    await onboarding.complete();
  }, [auth.user?.id, onboarding]);

  // Ürün turu: Toplantılar ekranı ilk kez açıldığında, yalnızca bir kez.
  const tourUserId = auth.user?.id ?? null;
  const [isTourOpen, setIsTourOpen] = useState(false);
  useEffect(() => {
    if (!isReady || !tourUserId || isTourOpen || currentView !== 'home' || selectedMeetingId || recording.isActive) return;
    if (!isTourPending(tourUserId)) return;
    // Takvim kartı ve liste yerleşsin, kutucuk doğru yeri göstersin.
    const timer = window.setTimeout(() => setIsTourOpen(true), 800);
    return () => window.clearTimeout(timer);
  }, [isReady, tourUserId, isTourOpen, currentView, selectedMeetingId, recording.isActive]);
  const finishTour = useCallback(() => {
    if (tourUserId) markTourDone(tourUserId);
    setIsTourOpen(false);
  }, [tourUserId]);
  const tourSteps = useMemo<TourStep[]>(
    () => [
      { targets: ['next-meeting'], text: t('Sıradaki toplantın burada.', 'Your next meeting shows up here.') },
      {
        targets: ['next-meeting-bot', 'next-meeting'],
        text: t('Bot, takvimindeki toplantılara kendiliğinden katılır.', 'The bot joins the meetings on your calendar by itself.'),
      },
      { targets: ['profile'], text: t('İstersen bunu Ayarlar’dan kapatabilirsin.', 'You can turn this off in Settings.') },
      {
        targets: ['record'],
        text: isMobile
          ? t('Yüz yüze toplantıları buradan kaydet.', 'Record in-person meetings here.')
          : t('Görüşmelerini buradan kaydet.', 'Record your calls here.'),
      },
      { targets: ['tab-calendar'], optional: true, text: t('Tüm toplantıların takvimde.', 'All your meetings are in the calendar.') },
      { targets: ['tab-chat'], optional: true, text: t('Toplantılarına soru sor.', 'Ask questions about your meetings.') },
      { text: t('Hepsi bu kadar. İyi toplantılar!', 'That’s it. Enjoy your meetings!') },
    ],
    [isMobile, t]
  );

  // Yüzen uyarıdaki "Kaydet" (takvim hatırlatıcısı / görüşme algılama):
  // etkinlik varsa kayıt ona bağlanır — başlık, bitiş saati ve katılımcılar.
  const promptRecordRef = useRef({ openRecording, handleStartRecordingForEvent });
  promptRecordRef.current = { openRecording, handleStartRecordingForEvent };
  useEffect(() => {
    return window.zabit.onMeetingPromptRecord((event) => {
      const current = promptRecordRef.current;
      if (event) current.handleStartRecordingForEvent(event);
      else current.openRecording();
    });
  }, []);

  /** Kayıtlı sohbetler: sunucuda şifreli geçmiş, her cihazda aynı (docs/sohbet-gecmisi-plani.md). */
  const chat = useChatHistory(isSignedIn, (message) => notify('error', message));

  const handleNavigateToChat = (initialPrompt?: string) => {
    // Bir toplantıdan ya da başka ekrandan gelen soru yeni bir sohbet açar. Sohbet ekranındayken
    // (masaüstünde mesaj kutusu Omnibar) soru açık sohbete eklenir; yeni sohbet "Yeni sohbet"ten.
    if (initialPrompt) {
      if (currentView !== 'chat') chat.newChat();
      void chat.send(initialPrompt);
    }
    setCurrentView('chat');
    setSelectedMeetingId(null);
  };

  // — Oturum kapıları —

  if (auth.status === 'checking') {
    return (
      <GateFrame>
        <div className="h-full w-full flex items-center justify-center text-neutral-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      </GateFrame>
    );
  }

  if (!isSignedIn) {
    return (
      <GateFrame>
        <SignInView
          onSignIn={() => void auth.signIn()}
          onSignedIn={() => void auth.refresh()}
          isSigningIn={auth.isSigningIn}
          error={auth.error}
        />
      </GateFrame>
    );
  }

  // Desteklenmeyen sürüm. Kayıt sürerken araya girmiyor; kapı kayıt bitince açılır.
  if (updateRequired !== undefined && !recording.isActive) {
    return (
      <GateFrame>
        <UpdateRequiredView minVersion={updateRequired} onSignOut={signOut} />
      </GateFrame>
    );
  }

  // Yasal onay ve ilk kurulum. Kayıt sürerken araya girmiyor: sunucu kayıt
  // ortasında "yeni metni onayla" derse kapı kayıt bitince açılır.
  if (!recording.isActive && onboarding.status !== 'done') {
    if (onboarding.status === 'error') {
      return (
        <GateFrame>
          <div className="h-full w-full flex items-center justify-center text-[#F0EEE6] px-6">
            <div className="w-full max-w-sm space-y-4 text-center">
              <p className="text-[13.5px] text-neutral-300 leading-relaxed">
                {t('Hesap bilgilerin yüklenemedi.', 'We couldn’t load your account.')}
              </p>
              <p className="text-[12px] text-rose-400 leading-relaxed">{onboarding.error}</p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => void onboarding.refresh()}
                  className="px-4 py-2 rounded-xl bg-[#F0EEE6] text-neutral-900 font-semibold text-[13px] hover:bg-white transition-colors"
                >
                  {t('Tekrar dene', 'Try again')}
                </button>
                <button
                  onClick={signOut}
                  className="px-4 py-2 rounded-xl text-[13px] text-neutral-400 hover:text-neutral-200 transition-colors"
                >
                  {t('Çıkış yap', 'Sign out')}
                </button>
              </div>
            </div>
          </div>
        </GateFrame>
      );
    }
    if (onboarding.status === 'required') {
      return (
        <GateFrame>
          <OnboardingView
            pending={onboarding.pending}
            needsSetup={onboarding.needsSetup}
            firstName={auth.user?.name.split(' ')[0] || null}
            onAccept={onboarding.accept}
            onComplete={completeOnboarding}
            onSignOut={signOut}
          />
        </GateFrame>
      );
    }
    return (
      <GateFrame>
        <div className="h-full w-full flex items-center justify-center text-neutral-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      </GateFrame>
    );
  }

  const isRecordingView = currentView === 'recording';
  // Not sayfası kendi kaydırmasını yönetiyor; kabuk ona dokunmuyor.
  const isNoteView = currentView === 'meeting' && Boolean(selectedMeeting);

  /** Telefonda geri düğmelerinin etiketi: dönülecek ekranın adı. */
  const viewLabel = (view: ViewMode): string => {
    switch (view) {
      case 'home': return t('Toplantılar', 'Meetings');
      case 'calendar': return t('Takvim', 'Calendar');
      case 'chat': return t('Sohbet', 'Chat');
      case 'more': return t('Daha fazla', 'More');
      case 'people': case 'person': return t('Kişiler', 'People');
      case 'companies': case 'company': return t('Şirketler', 'Companies');
      case 'integrations': return t('Entegrasyonlar', 'Integrations');
      case 'trash': return t('Çöp kutusu', 'Trash');
      case 'daily-digest': return t('Günlük Özet', 'Daily Digest');
      default: return t('Geri', 'Back');
    }
  };

  const settingsPage = (
    <SettingsPage
      key={settingsEntry?.section ?? 'default'}
      isDarkMode={isDarkMode}
      initialSection={settingsEntry?.section}
      backLabel={viewLabel(returnView)}
      onBack={() => {
        setSettingsEntry(null);
        setCurrentView(isMobile ? returnView : 'home');
        // Abonelikte plan değişmiş olabilir (ödeme, kupon) — bot kilidi güncellensin.
        void auth.refresh();
      }}
      onProfileSaved={() => void auth.refresh()}
      onNotify={notify}
      onSignOut={signOut}
    />
  );

  /** Ekranların içeriği — masaüstü ve telefon düzeni aynı ekranları paylaşıyor. */
  const pageContent = (
    <>
      {currentView === 'home' &&
        (isMobile ? (
          <MobileHomeView
            user={auth.user}
            meetings={meetings}
            isLoading={meetingsStore.isLoading}
            error={meetingsStore.error}
            onSelectMeeting={handleSelectMeeting}
            onOpenProfile={() => openSettings('general')}
            onError={(text) => showToast({ kind: 'error', text })}
          />
        ) : (
          <HomeView
            meetings={meetings}
            onSelectMeeting={handleSelectMeeting}
            isDarkMode={isDarkMode}
            onDeleteMeetings={handleDeleteMeetings}
            onRecordSystemAudioForEvent={handleRecordSystemAudioForEvent}
            onError={(text) => showToast({ kind: 'error', text })}
            isLoading={meetingsStore.isLoading}
            error={meetingsStore.error}
          />
        ))}

      {/* Seçili toplantı silindiyse (ya da hiç yüklenemediyse)
          boş bir sayfa yerine ne olduğunu söyleyen bir durum. */}
      {currentView === 'meeting' && !selectedMeeting && meetingsStore.isLoading && (
        <div className="py-16 flex justify-center text-neutral-500">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}
      {currentView === 'meeting' && !selectedMeeting && !meetingsStore.isLoading && (
        <div className="max-w-3xl mx-auto w-full px-6 py-16 text-center space-y-3">
          <FileX className="w-8 h-8 text-neutral-600 mx-auto" />
          <p className="text-[13.5px] text-neutral-500">
            {t('Bu toplantı artık mevcut değil.', 'This meeting no longer exists.')}
          </p>
          <button
            onClick={handleBackToHome}
            className="text-[13px] font-semibold text-[#46CBE2] hover:text-[#7BDAEA] transition-colors"
          >
            {t('Ana sayfaya dön', 'Back to home')}
          </button>
        </div>
      )}

      {currentView === 'calendar' && (
        <div className={isMobile ? '' : 'max-w-3xl mx-auto w-full py-4'}>
          <MobileCalendarView
            meetings={meetings}
            onSelectMeeting={handleSelectMeeting}
            onError={(text) => showToast({ kind: 'error', text })}
          />
        </div>
      )}

      {currentView === 'more' && isMobile && (
        <MobileMoreView
          user={auth.user}
          isAdmin={auth.isAdmin}
          onNavigate={(view) => setCurrentView(view)}
          onOpenProfile={() => openSettings('general')}
          onOpenSettings={() => openSettings()}
          onOpenSubscription={() => openSettings('subscription')}
          onOpenHelp={() => openSettings('help')}
          onSignOut={signOut}
        />
      )}

      {currentView === 'daily-digest' && (
        <DailyDigestView
          meetings={meetings}
          onSelectMeeting={handleSelectMeeting}
          isDarkMode={isDarkMode}
          onAskAI={handleNavigateToChat}
          onUpdateMeeting={handleUpdateMeeting}
          onNotify={notify}
        />
      )}

      {currentView === 'chat' && (
        <AIChatView
          messages={chat.messages}
          onSendMessage={chat.send}
          meetings={meetings}
          onSelectMeeting={handleSelectMeeting}
          isDarkMode={isDarkMode}
          isResponding={chat.isResponding}
          isMobile={isMobile}
          isOpening={chat.isOpening}
          chats={{
            threads: chat.threads,
            activeId: chat.threadId,
            onNew: chat.newChat,
            onOpen: (id) => void chat.openThread(id),
            onRename: chat.renameThread,
            onDelete: chat.deleteThread,
            onDeleteAll: chat.deleteAll,
          }}
        />
      )}

      {currentView === 'people' && (
        <PeopleView isDarkMode={isDarkMode} revision={directoryRevision} onOpenPerson={handleOpenPerson} />
      )}

      {currentView === 'person' && selectedPersonId && (
        <PersonProfileView
          key={selectedPersonId}
          personId={selectedPersonId}
          isDarkMode={isDarkMode}
          revision={directoryRevision}
          onBack={() => setCurrentView('people')}
          onOpenMeeting={handleOpenMeetingById}
          onOpenCompany={handleOpenCompany}
          onAskAI={handleNavigateToChat}
          onChanged={bumpDirectory}
          onNotify={notify}
        />
      )}

      {currentView === 'companies' && (
        <CompaniesView
          isDarkMode={isDarkMode}
          revision={directoryRevision}
          onOpenCompany={handleOpenCompany}
          onChanged={bumpDirectory}
          onNotify={notify}
        />
      )}

      {currentView === 'company' && selectedCompanyId && (
        <CompanyProfileView
          key={selectedCompanyId}
          companyId={selectedCompanyId}
          isDarkMode={isDarkMode}
          revision={directoryRevision}
          onBack={() => setCurrentView('companies')}
          onOpenMeeting={handleOpenMeetingById}
          onOpenPerson={handleOpenPerson}
          onAskAI={handleNavigateToChat}
          onChanged={bumpDirectory}
          onNotify={notify}
        />
      )}

      {currentView === 'integrations' && (
        <IntegrationsView onNotify={notify} />
      )}

      {currentView === 'trash' && <TrashView isDarkMode={isDarkMode} onNotify={notify} />}

      {currentView === 'folder' && selectedFolder && (
        <FolderView
          key={selectedFolder.id}
          folder={selectedFolder}
          meetings={meetings.filter((meeting) => meeting.folderId === selectedFolder.id)}
          onSelectMeeting={handleSelectMeeting}
          onRename={(name) => foldersStore.rename(selectedFolder.id, name)}
          onDelete={async () => {
            await foldersStore.remove(selectedFolder.id);
            meetingsStore.forgetFolder(selectedFolder.id);
            setSelectedFolderId(null);
            setCurrentView('home');
          }}
          onRemoveMeeting={(meeting) => handleMoveMeeting(meeting.id, null)}
          onError={(text) => showToast({ kind: 'error', text })}
        />
      )}

      {/* Ayarlar yalnızca yöneticilere. Rol değişirse görünüm de düşer. */}
      {currentView === 'settings' && auth.isAdmin && (
        <SettingsView isDarkMode={isDarkMode} />
      )}
    </>
  );

  /** Pencereler ve bildirimler — iki düzende de aynı. */
  const overlays = (
    <>
      {autoStop.warning && (
        <AutoStopBanner
          warning={autoStop.warning}
          onKeepRecording={autoStop.keepRecording}
          onStopNow={autoStop.stopNow}
          isDarkMode={isDarkMode}
        />
      )}

      {sourceChoice && (
        <RecordSourceDialog
          isDarkMode={isDarkMode}
          onClose={() => setSourceChoice(null)}
          onChoose={(withTab) => {
            const prefill = sourceChoice.prefill;
            setSourceChoice(null);
            // Aynı tıklamanın içinde: tarayıcı paylaşım penceresini ancak böyle açıyor.
            openRecording({ ...prefill, captureSystemAudio: withTab });
          }}
          onSendBot={() => {
            setSourceChoice(null);
            setIsBotDialogOpen(true);
          }}
        />
      )}

      {isBotDialogOpen && (
        <SendBotDialog
          isDarkMode={isDarkMode}
          onClose={() => setIsBotDialogOpen(false)}
          onSend={handleSendBotFromDialog}
        />
      )}

      {selectedMeeting && window.zabit.meetingShare && (
        <ShareMeetingDialog
          open={isShareOpen}
          api={window.zabit.meetingShare}
          targetId={selectedMeeting.id}
          title={selectedMeeting.title}
          onClose={() => setIsShareOpen(false)}
        />
      )}

      {isMobile && (
        <MobileRecordSheet
          open={isRecordSheetOpen}
          onClose={() => setIsRecordSheetOpen(false)}
          onRecord={() => {
            setIsRecordSheetOpen(false);
            requestRecording();
          }}
          onSendBot={() => {
            setIsRecordSheetOpen(false);
            setIsBotDialogOpen(true);
          }}
          onUpload={() => {
            setIsRecordSheetOpen(false);
            void handleUploadAudio();
          }}
        />
      )}

      <Toast toast={toast} onDismiss={() => setToast(null)} isDarkMode={isDarkMode} />

      {isTourOpen && <ProductTour steps={tourSteps} onDone={finishTour} />}
    </>
  );

  // — Telefon düzeni —
  if (isMobile && currentView !== 'preferences') {
    const mobileTab: MobileTab | null =
      currentView === 'home'
        ? 'home'
        : currentView === 'calendar' || currentView === 'daily-digest'
          ? 'calendar'
          : currentView === 'chat'
            ? 'chat'
            : MORE_TAB_VIEWS.includes(currentView)
              ? 'more'
              : null;
    // Kişi ve şirket profillerinin kendi geri düğmesi var; listelerin yok.
    const secondaryBack: ViewMode | null = MORE_LIST_VIEWS.includes(currentView)
      ? 'more'
      : currentView === 'daily-digest'
        ? 'calendar'
        : null;

    return (
      <div className="h-dvh flex flex-col overflow-hidden select-none bg-background text-foreground">
        {isRecordingView ? (
          <>
            <MobileRecordingTopBar
              phase={recording.phase}
              isPaused={recording.isPaused}
              onMinimize={() => setCurrentView(returnView === 'recording' ? 'home' : returnView)}
              onDiscard={() => void handleDiscardRecording()}
            />
            <main className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <RecordingView session={recording} isDarkMode={isDarkMode} focusNotesSignal={notesFocusSignal} />
            </main>
            <MobileRecordingControls
              phase={recording.phase}
              isPaused={recording.isPaused}
              seconds={recording.seconds}
              micLevel={recording.micLevel}
              onTogglePause={recording.togglePause}
              onStop={() => void handleStopRecording()}
            />
          </>
        ) : isNoteView ? (
          <main className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <CraftNoteView
              key={selectedMeeting!.id}
              meeting={selectedMeeting!}
              onUpdateMeeting={handleUpdateMeeting}
              isDarkMode={isDarkMode}
              onBackToHome={handleBackToHome}
              onSendSummaryEmail={handleSendSummaryEmail}
              onShare={shareMeeting}
              onDeleteMeeting={(meeting) => handleDeleteMeetings([meeting.id])}
              mobileBackLabel={viewLabel(returnView)}
            />
          </main>
        ) : (
          <>
            {secondaryBack && <MobileBackBar label={viewLabel(secondaryBack)} onBack={() => setCurrentView(secondaryBack)} />}
            <main
              className={
                currentView === 'chat'
                  ? 'flex-1 min-h-0 flex flex-col overflow-hidden'
                  : 'flex-1 min-h-0 overflow-y-auto overscroll-contain'
              }
            >
              {pageContent}
            </main>
            {audioUpload.phase && (
              <div className="shrink-0 mx-3 mb-2 h-11 rounded-2xl border border-border bg-card flex items-center gap-2.5 px-4 text-[14px] text-foreground">
                <Loader2 className="w-4 h-4 animate-spin text-brand-text" />
                <span className="truncate">{uploadPhaseLabel(audioUpload.phase)}</span>
              </div>
            )}
            {recording.isActive && (
              <MobileRecordingStrip
                title={recording.title}
                seconds={recording.seconds}
                isPaused={recording.isPaused}
                onOpen={() => setCurrentView('recording')}
              />
            )}
            <MobileTabBar
              active={mobileTab}
              onNavigate={(tab) => {
                setSelectedMeetingId(null);
                setCurrentView(tab);
              }}
              onRecord={() => (recording.isActive ? setCurrentView('recording') : setIsRecordSheetOpen(true))}
              isRecording={recording.isActive}
            />
          </>
        )}
        {overlays}
      </div>
    );
  }

  // — Masaüstü düzeni —
  return (
    <div className={`h-dvh flex overflow-hidden select-none transition-colors duration-200 ${
      isDarkMode ? 'bg-[#111113] text-[#F0EEE6]' : 'bg-[#FAFAFA] text-[#1F2937]'
    }`}>
      {currentView === 'preferences' ? (
        // Ayarlar tam sayfa: ana kenar çubuğunun yerini kendi bölüm listesi alıyor.
        settingsPage
      ) : (
        <>
        <Sidebar
          // Profil sayfaları kendi listelerinin menü öğesini vurgulasın.
          currentView={currentView === 'person' ? 'people' : currentView === 'company' ? 'companies' : currentView}
          onNavigate={(view) => {
            setCurrentView(view);
            setSelectedMeetingId(null);
            // "Ayarlar" her zaman baştan açılsın, son açılan Abonelik bölümünde kalmasın.
            setSettingsEntry(null);
          }}
          meetings={meetings}
          selectedMeetingId={selectedMeeting?.id}
          onSelectMeeting={handleSelectMeeting}
          onNewNote={() => void handleCreateNewNote()}
          foldersEnabled={foldersStore.enabled}
          folders={foldersStore.folders}
          selectedFolderId={currentView === 'folder' ? selectedFolderId : null}
          onCreateFolder={async (name) => {
            try {
              handleSelectFolder((await foldersStore.create(name)).id);
            } catch (err) {
              showToast({ kind: 'error', text: (err as Error).message });
            }
          }}
          onSelectFolder={handleSelectFolder}
          onMoveMeeting={handleMoveMeeting}
          isOpen={sidebarOpen}
          isDarkMode={isDarkMode}
          onToggleTheme={toggleTheme}
          user={auth.user}
          isAdmin={auth.isAdmin}
          onSignOut={signOut}
          onOpenSubscription={() => {
            setSelectedMeetingId(null);
            openSettings('subscription');
          }}
          isRecording={recording.isActive}
          recordingSeconds={recording.seconds}
          recordingTitle={recording.title}
          onOpenRecording={() => setCurrentView('recording')}
        />

        <div className="flex-1 flex flex-col overflow-hidden">
          {isRecordingView ? (
            <RecordingTopBar
              phase={recording.phase}
              isPaused={recording.isPaused}
              seconds={recording.seconds}
              micLevel={recording.micLevel}
              systemLevel={recording.systemLevel}
              hasSystemAudio={recording.hasSystemAudio}
              isDarkMode={isDarkMode}
              onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
              onBack={handleBackToHome}
              onDiscard={() => void handleDiscardRecording()}
              onTogglePause={recording.togglePause}
              onStop={() => void handleStopRecording()}
            />
          ) : (
            <TopBar
              onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
              onOpenOmnibar={() => document.getElementById('omnibar-input')?.focus()}
              onStartRecord={() => requestRecording()}
              onUploadAudio={() => void handleUploadAudio()}
              uploadStatus={audioUpload.phase ? uploadPhaseLabel(audioUpload.phase) : null}
              onSendBot={() => setIsBotDialogOpen(true)}
              isRecording={recording.isActive}
              isDarkMode={isDarkMode}
              onToggleTheme={toggleTheme}
              currentMeeting={selectedMeeting}
              onBackToHome={handleBackToHome}
            />
          )}

          <main className="flex-1 min-h-0 flex flex-col overflow-hidden relative">
            {isRecordingView ? (
              <RecordingView session={recording} isDarkMode={isDarkMode} focusNotesSignal={notesFocusSignal} />
            ) : isNoteView ? (
              /**
               * Not sayfası kendi içinde kayıyor (`h-full` + iç `overflow-y-auto`).
               * Bu yüzden dışarıda ne kaydırma kabı ne de alt dolgu var.
               *
               * Eskiden burada da `pb-24` uygulanıyordu: Omnibar bu sayfada hiç
               * çizilmediği için o 96 piksel boşa gidiyor ve notun altında,
               * sayfa arka planı renginde ölü bir şerit bırakıyordu — ekranda
               * "gereksiz siyah boşluk" olarak görünen şey buydu.
               */
              <CraftNoteView
                key={selectedMeeting!.id}
                meeting={selectedMeeting!}
                onUpdateMeeting={handleUpdateMeeting}
                isDarkMode={isDarkMode}
                onBackToHome={handleBackToHome}
                onSendSummaryEmail={handleSendSummaryEmail}
                onShare={shareMeeting}
                onDeleteMeeting={(meeting) => handleDeleteMeetings([meeting.id])}
              />
            ) : (
              <>
                {/* Omnibar sayfanın üstünde yüzdüğü için içerik onun altında
                    kalmasın diye alt dolgu var — yalnızca Omnibar'ın çizildiği
                    sayfalarda. */}
                <div className="flex-1 min-h-0 overflow-y-auto pb-28">{pageContent}</div>

                <div className="absolute bottom-4 left-0 right-0 px-4 pointer-events-none z-30 flex justify-center">
                  <div className="pointer-events-auto w-full max-w-2xl">
                    <Omnibar
                      meetings={meetings}
                      onSelectMeeting={handleSelectMeeting}
                      onStartRecord={() => requestRecording()}
                      onNavigateToChat={handleNavigateToChat}
                      isChatBusy={currentView === 'chat' && chat.isResponding}
                      onToggleTheme={toggleTheme}
                      isDarkMode={isDarkMode}
                    />
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
        </>
      )}

      {overlays}
    </div>
  );
}

/** Telefonda "Daha fazla" sekmesinin altındaki ekranlar (sekme vurgusu için). */
const MORE_TAB_VIEWS: ViewMode[] = ['more', 'people', 'person', 'companies', 'company', 'integrations', 'trash', 'settings'];
/** Bunların kendi geri düğmesi yok: telefonda üstlerine "‹ Daha fazla" konuyor. */
const MORE_LIST_VIEWS: ViewMode[] = ['people', 'companies', 'integrations', 'trash', 'settings'];
